import React from 'react';

// Legend bar for the design-document viewers, styled like the threat-model legend bar.
// The C4 diagrams carry no key of their own (2026-10-29): the shapes here match the
// c4model.com style of the v10 diagrams (threatpen-study/tools/v10/c4_style.py).

const BLUE = '#1168BD', GREY = '#6B6B6B', GREEN = '#2E7D0F', LINE = '#444';
const UC_FILL = '#7acff5';

const svg = (w, h, children) => (
  <svg width={w} height={h} style={{ overflow: 'visible', display: 'block' }}>{children}</svg>
);
const box = (color, extra = {}) => (
  <div style={{ width: 22, height: 14, border: `1.5px solid ${color}`, borderRadius: 3, background: '#fff', ...extra }} />
);
const arrow = (p, id, { dashed = false, head = 'filled', color = LINE } = {}) => (
  svg(26, 10, <>
    <defs>
      <marker id={`${p}${id}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
        {head === 'filled' && <path d="M0,0 L6,3.5 L0,7 Z" fill={color} />}
        {head === 'open' && <path d="M0,0 L6,3.5 L0,7" fill="none" stroke={color} strokeWidth="1.2" />}
        {head === 'hollow' && <path d="M0.5,0.5 L6,3.5 L0.5,6.5 Z" fill="#fff" stroke={color} strokeWidth="1.2" />}
      </marker>
    </defs>
    <line x1="1" y1="5" x2="22" y2="5" stroke={color} strokeWidth="1.5"
      strokeDasharray={dashed ? '4 3' : undefined} markerEnd={head ? `url(#${p}${id})` : undefined} />
  </>)
);

const C4_ITEMS = {
  person: [svg(18, 18, <>
    <rect x="1" y="8" width="16" height="9.5" rx="4" fill="#fff" stroke={GREEN} strokeWidth="1.5" />
    <circle cx="9" cy="5" r="4" fill="#fff" stroke={GREEN} strokeWidth="1.5" />
  </>), 'Person'],
  system: [box(BLUE), 'Software System'],
  external: [box(GREY), 'External Software System'],
  boundary: [svg(26, 17, <>
    <rect x="1" y="1" width="24" height="15" rx="4" fill="none" stroke={BLUE} strokeWidth="1.5" />
    <line x1="4" y1="12.5" x2="11" y2="12.5" stroke={BLUE} strokeWidth="1.5" />
  </>), 'Boundary'],
  webapp: [svg(24, 16, <>
    <rect x="0.75" y="0.75" width="22.5" height="14.5" rx="2" fill={BLUE} />
    <rect x="2.5" y="5" width="19" height="8.5" rx="1" fill="#fff" />
  </>), 'Container: web application'],
  app: [<div style={{ ...{ width: 24, height: 15, border: `1.5px solid ${BLUE}`, borderRadius: 3, background: '#fff' },
    fontSize: 7, fontWeight: 700, color: BLUE, lineHeight: '9px', paddingLeft: 2 }}>&gt;_</div>,
    'Container: server-side application'],
  database: [svg(24, 16, <>
    <ellipse cx="12" cy="13" rx="11" ry="3" fill="#fff" stroke={BLUE} strokeWidth="1.5" />
    <rect x="1" y="4" width="22" height="9" fill="#fff" stroke="none" />
    <line x1="1" y1="4" x2="1" y2="13" stroke={BLUE} strokeWidth="1.5" />
    <line x1="23" y1="4" x2="23" y2="13" stroke={BLUE} strokeWidth="1.5" />
    <ellipse cx="12" cy="4" rx="11" ry="3" fill="#fff" stroke={BLUE} strokeWidth="1.5" />
  </>), 'Container: database'],
  component: [svg(26, 16, <>
    <rect x="4" y="1" width="21" height="14" fill="#fff" stroke={BLUE} strokeWidth="1.5" />
    <rect x="1" y="3.5" width="6" height="3" fill="#fff" stroke={BLUE} strokeWidth="1.2" />
    <rect x="1" y="9.5" width="6" height="3" fill="#fff" stroke={BLUE} strokeWidth="1.2" />
  </>), 'Component'],
};

// Element types per C4 tab: the union of what the CSS, HPM and sample diagrams of that level use
const C4_TABS = {
  context:   ['person', 'system', 'external'],
  container: ['person', 'boundary', 'webapp', 'app', 'database', 'external'],
  component: ['boundary', 'component', 'webapp', 'database', 'system', 'external'],
};

const UC_ELEMENTS = [
  [svg(12, 18, <g fill="none" stroke="#000" strokeWidth="1.2">
    <circle cx="6" cy="3.5" r="2.8" fill={UC_FILL} />
    <line x1="6" y1="6.3" x2="6" y2="12" /><line x1="1" y1="8.5" x2="11" y2="8.5" />
    <line x1="6" y1="12" x2="2" y2="17.5" /><line x1="6" y1="12" x2="10" y2="17.5" />
  </g>), 'Actor'],
  [<div style={{ width: 30, height: 15, border: '1.2px solid #000', borderRadius: '50%', background: UC_FILL }} />, 'Use Case'],
  [<div style={{ width: 22, height: 14, border: '1.2px solid #000', background: UC_FILL }} />, 'System Boundary'],
  [<span style={{ fontSize: 10, fontWeight: 700, color: '#000', background: '#fff', border: '1px solid #ddd', padding: '0 3px' }}>/path</span>,
    'Routes of a use case'],
  [svg(18, 16, <path d="M1,1 H12 L17,6 V15 H1 Z M12,1 V6 H17" fill="#e6e6e6" stroke="#000" strokeWidth="1.1" />), 'Note'],
];

const Section = ({ title, items }) => (
  <>
    <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700, letterSpacing: '0.05em', flexShrink: 0 }}>{title}</span>
    {items.map(([visual, label]) => (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        {visual}<span style={{ whiteSpace: 'nowrap' }}>{label}</span>
      </div>
    ))}
  </>
);

export default function DesignDocLegendBar({ tab, idPrefix = '' }) {
  const p = `${idPrefix}docleg-`;
  const isUc = tab === 'uc';
  const elements = isUc ? UC_ELEMENTS : (C4_TABS[tab] || []).map(k => C4_ITEMS[k]);
  const relationships = isUc
    ? [[arrow(p, 'assoc', { head: null, color: '#000' }), 'Association'],
       [arrow(p, 'dep', { dashed: true, head: 'open', color: '#000' }), '«include» / «extend»'],
       [arrow(p, 'gen', { head: 'hollow', color: '#000' }), 'Generalisation']]
    : [[arrow(p, 'rel', { dashed: true }), 'Relationship']];

  return (
    <div id={`${idPrefix}doc-legend-bar`} style={{
      padding: '3px 14px', borderBottom: '1px solid #eee', background: '#fafafa',
      display: 'flex', alignItems: 'center', flexShrink: 0,
      fontSize: 12, color: '#666', overflow: 'visible',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
        <Section title="ELEMENTS" items={elements} />
      </div>
      <div style={{ width: 1, height: 16, background: '#ddd', margin: '0 10px', flexShrink: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <Section title="RELATIONSHIPS" items={relationships} />
      </div>
    </div>
  );
}
